import React, { useMemo } from "react";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function toDate(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

function getInvoiceTotal(invoice) {
  const total = Number(
    invoice?.total ??
      invoice?.totalAmount ??
      invoice?.grandTotal ??
      invoice?.invoiceTotal ??
      invoice?.amount ??
      invoice?.subtotal
  );

  return Number.isFinite(total)
    ? total
    : 0;
}

function getAmountPaid(invoice) {
  const paid = Number(
    invoice?.amountPaid ??
      invoice?.paidAmount ??
      invoice?.paymentsTotal
  );

  const status = normalize(
    invoice?.status ||
      invoice?.invoiceStatus
  );

if (
  !Number.isFinite(paid) &&
  status === "paid"
) {
  return getInvoiceTotal(invoice);
}

  return Number.isFinite(paid)
    ? paid
    : 0;
}

function getInvoiceStatus(invoice) {
  const status = normalize(
    invoice?.status ||
      invoice?.invoiceStatus ||
      "draft"
  );

  const dueDate = toDate(
    invoice?.dueDate ||
      invoice?.paymentDueDate
  );

  const overdueEligibleStatuses = [
    "sent",
    "submitted",
    "open",
    "outstanding",
    "due",
    "partial",
    "partially_paid",
  ];

  if (
    overdueEligibleStatuses.includes(status) &&
    dueDate &&
    dueDate.getTime() < Date.now()
  ) {
    return "overdue";
  }

  return status;
}

function formatMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value) || 0);
}

function formatDate(value) {
  const date = toDate(value);

  if (!date) return "—";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function getStatusTone(status) {
  const normalizedStatus = normalize(status);

 if (
  normalizedStatus === "paid"
) {
    return {
      color: "#166534",
      background: "#dcfce7",
    };
  }

  if (normalizedStatus === "overdue") {
    return {
      color: "#991b1b",
      background: "#fee2e2",
    };
  }

  if (
    ["sent", "submitted"].includes(
      normalizedStatus
    )
  ) {
    return {
      color: "#1d4ed8",
      background: "#dbeafe",
    };
  }

  return {
    color: "#92400e",
    background: "#fef3c7",
  };
}

function MetricCard({
  label,
  value,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 20,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function Facility360Invoices({
  facility,
  invoices = [],
}) {
  const facilityId = normalize(
    facility?.id || facility?.facilityId
  );

  const facilityInvoices = useMemo(() => {
    return (
      Array.isArray(invoices) ? invoices : []
    )
      .filter(
        (invoice) =>
          normalize(
            invoice?.facilityId ||
              invoice?.facilityID
          ) === facilityId
      )
      .sort((a, b) => {
        const aDate = toDate(
          a?.invoiceDate ||
            a?.createdAt ||
            a?.updatedAt
        );

        const bDate = toDate(
          b?.invoiceDate ||
            b?.createdAt ||
            b?.updatedAt
        );

        return (
          (bDate?.getTime() || 0) -
          (aDate?.getTime() || 0)
        );
      });
  }, [invoices, facilityId]);

  const summary = useMemo(() => {
    return facilityInvoices.reduce(
      (totals, invoice) => {
        const total =
          getInvoiceTotal(invoice);

        const amountPaid =
          getAmountPaid(invoice);

        const balance = Math.max(
          0,
          total - amountPaid
        );

        const status =
          getInvoiceStatus(invoice);

        totals.totalInvoiced += total;
        totals.totalPaid += amountPaid;
        totals.outstanding += balance;

        if (status === "draft") {
          totals.draftCount += 1;
        }

        if (
          ["sent", "submitted"].includes(status)
        ) {
          totals.sentCount += 1;
        }

        if (status === "overdue") {
          totals.overdueCount += 1;
          totals.overdueBalance += balance;
        }

     if (status === "paid") {
  totals.paidCount += 1;
}

        return totals;
      },
      {
        totalInvoiced: 0,
        totalPaid: 0,
        outstanding: 0,
        overdueBalance: 0,
        draftCount: 0,
        sentCount: 0,
        overdueCount: 0,
        paidCount: 0,
      }
    );
  }, [facilityInvoices]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Invoices
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Actual invoice records for{" "}
          {facility?.name ||
            facility?.facilityName ||
            facility?.id ||
            "this facility"}
          .
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="Total Invoiced"
          value={formatMoney(
            summary.totalInvoiced
          )}
          color="#1d4ed8"
        />

        <MetricCard
          label="Total Paid"
          value={formatMoney(
            summary.totalPaid
          )}
          color="#166534"
        />

        <MetricCard
          label="Outstanding"
          value={formatMoney(
            summary.outstanding
          )}
          color={
            summary.outstanding > 0
              ? "#b45309"
              : "#166534"
          }
        />

        <MetricCard
          label="Overdue Balance"
          value={formatMoney(
            summary.overdueBalance
          )}
          color={
            summary.overdueBalance > 0
              ? "#b91c1c"
              : "#166534"
          }
        />

        <MetricCard
          label="Draft"
          value={summary.draftCount}
        />

        <MetricCard
          label="Sent"
          value={summary.sentCount}
          color="#1d4ed8"
        />

        <MetricCard
          label="Overdue"
          value={summary.overdueCount}
          color={
            summary.overdueCount > 0
              ? "#b91c1c"
              : "#166534"
          }
        />

        <MetricCard
          label="Paid"
          value={summary.paidCount}
          color="#166534"
        />
      </div>

      {facilityInvoices.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No invoice records were found for this facility.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {facilityInvoices.map(
            (invoice, index) => {
              const total =
                getInvoiceTotal(invoice);

              const amountPaid =
                getAmountPaid(invoice);

              const balance = Math.max(
                0,
                total - amountPaid
              );

              const status =
                getInvoiceStatus(invoice);

              const tone =
                getStatusTone(status);

              const factoringCompany =
                invoice?.factoringCompany ||
                invoice?.remitTo ||
                "";

              const factoringStatus =
                invoice?.factoringStatus ||
                "";

              return (
                <div
                  key={
                    invoice.id ||
                    invoice.invoiceId ||
                    `${facilityId}-${index}`
                  }
                  style={{
                    padding: 13,
                    borderRadius: 14,
                    background: "#ffffff",
                    border:
                      "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: 14,
                          fontWeight: 950,
                        }}
                      >
                        Invoice{" "}
                        {invoice.invoiceNumber ||
                          invoice.number ||
                          invoice.id ||
                          "—"}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: "#64748b",
                          fontSize: 11,
                          fontWeight: 750,
                        }}
                      >
                        Invoice date:{" "}
                        {formatDate(
                          invoice.invoiceDate ||
                            invoice.createdAt
                        )}
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          color: "#64748b",
                          fontSize: 11,
                          fontWeight: 750,
                        }}
                      >
                        Billing period:{" "}
                        {formatDate(
                          invoice.billingStart ||
                            invoice.weekStart
                        )}
                        {" – "}
                        {formatDate(
                          invoice.billingEnd ||
                            invoice.weekEnd
                        )}
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          color: "#475569",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        Due:{" "}
                        {formatDate(
                          invoice.dueDate ||
                            invoice.paymentDueDate
                        )}
                      </div>

                      {factoringCompany ? (
                        <div
                          style={{
                            marginTop: 5,
                            color: "#7c3aed",
                            fontSize: 10,
                            fontWeight: 800,
                          }}
                        >
                          Factoring:{" "}
                          {titleCase(
                            factoringCompany
                          )}
                          {factoringStatus
                            ? ` · ${titleCase(
                                factoringStatus
                              )}`
                            : ""}
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={{
                        textAlign: "right",
                      }}
                    >
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: 15,
                          fontWeight: 950,
                        }}
                      >
                        {formatMoney(total)}
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          color: "#64748b",
                          fontSize: 10,
                          fontWeight: 750,
                        }}
                      >
                        Balance:{" "}
                        {formatMoney(balance)}
                      </div>

                      <div
                        style={{
                          display: "inline-block",
                          marginTop: 6,
                          padding: "5px 8px",
                          borderRadius: 999,
                          color: tone.color,
                          background:
                            tone.background,
                          fontSize: 9,
                          fontWeight: 950,
                          textTransform:
                            "uppercase",
                        }}
                      >
                        {titleCase(status)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}